import { Link, useParams } from "react-router-dom";
import { CircleAlert } from "lucide-react";

import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import ACL_RELATIONSHIPS from "acl-relationships";
import { upperFirst } from "helpers/string";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import { useCustomTableSchema } from "hooks/useCustomTableSchema";
import CustomTableGrid from "routes/custom_tables/grid/CustomTableGrid";
import TableIconBadge from "routes/custom_tables/TableIconBadge";

const TableDataPage = () => {
  const { slug } = useParams();
  const canRead = useAuthorized([ACL_RELATIONSHIPS.customTable.readData]);
  const { schema, isFetched, isError } = useCustomTableSchema(slug);

  usePageTitle(
    schema?.display_name ? upperFirst(schema.display_name) : "Custom Tables"
  );

  if (!isFetched) {
    return null;
  }

  if (!canRead) {
    return (
      <div className="tw:w-full">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CircleAlert />
            </EmptyMedia>
            <EmptyTitle>Access denied</EmptyTitle>
            <EmptyDescription>
              You don&apos;t have permission to view this table&apos;s data.
            </EmptyDescription>
          </EmptyHeader>
          <Link to="/">Back to home</Link>
        </Empty>
      </div>
    );
  }

  if (isError || !schema) {
    return (
      <div className="tw:w-full">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CircleAlert />
            </EmptyMedia>
            <EmptyTitle>Table not found</EmptyTitle>
            <EmptyDescription>
              We couldn&apos;t find a custom table called <code>{slug}</code>.
            </EmptyDescription>
          </EmptyHeader>
          <Link to="/">Back to home</Link>
        </Empty>
      </div>
    );
  }

  const headerLabel = upperFirst(schema.display_name);

  return (
    <CustomTableGrid
      slug={slug}
      header={headerLabel}
      headerIcon={<TableIconBadge iconName={schema.icon_name} />}
    />
  );
};

export default TableDataPage;
